import { CompanySettings } from "@mcleod/core";
import { Button } from "../components/button/Button";
import { ButtonProps } from "../components/button/ButtonProps";
import { ButtonVariant } from "../components/button/ButtonVariant";
import { Panel } from "../components/panel/Panel";
import { PanelProps } from "../components/panel/PanelProps";
import { Textbox } from "../components/textbox/Textbox";
import { TextboxProps } from "../components/textbox/TextboxProps";
import { DataSource } from "../databinding/DataSource";
import { SlideoutDecoratorProps } from "./decorators/SlideoutDecoratorProps";

export enum PanelProcessLastRunVariant {
    stacked = "stacked",
    inline = "inline",
    compact = "compact"
}

export interface PanelProcessLastRunProps extends PanelProps {
    variant: PanelProcessLastRunVariant;
}

export class PanelProcessLastRun extends Panel implements PanelProcessLastRunProps {
    private _variant: PanelProcessLastRunVariant;
    private static processLogViewerHandler: (processDescription: string, props?: Partial<SlideoutDecoratorProps>) => void;

    private readonly processDescription: string;
    private readonly sourceProcessLog: DataSource = new DataSource({url: "lib/process-last-run"});
    private initialized: boolean = false;

    private readonly textboxProps: Partial<TextboxProps> = {
        fontSize: "small",
        printable: true,
        rowBreak: false,
        fontBold: true,
        dataSource: this.sourceProcessLog
    }

    private constructor(processDescription: string, props: Partial<PanelProcessLastRunProps> = {}) {
        super(props, false);
        this.processDescription = processDescription;
        props.id ??= "panelProcessLastRun";
        props.rowBreak ??= false;
        props.variant ??= PanelProcessLastRunVariant.inline;
        this.setProps(props);
        this.initialized = true;
        this.syncUI();
    }

    override syncInnerComponentIds() {
        this.syncUI();
    }

    public static create(processDescription: string, props: Partial<PanelProcessLastRunProps> = {}): PanelProcessLastRun {
        if (CompanySettings.getSingleton().isProcessLogEnabled() === true) {
            return new PanelProcessLastRun(processDescription, props);
        }
        return null;
    }

    public static registerProcessLogViewerHandler(handler: (processDescription: string, props?: Partial<SlideoutDecoratorProps>) => void): void {
        PanelProcessLastRun.processLogViewerHandler = handler;
    }

    private get isCompact(): boolean {
        return this.variant === PanelProcessLastRunVariant.compact;
    }

    public get variant(): PanelProcessLastRunVariant {
        return this._variant ?? PanelProcessLastRunVariant.inline;
    }

    public set variant(value: PanelProcessLastRunVariant) {
        if (this._variant !== value) {
            this._variant = value;
            this.syncUI();
        }
    }

    private syncUI() {
        if (!this.initialized) {
            return;
        }
        this.removeAll();
        this.add(this.createButtonProcessLog());
        if (!this.isCompact) {
            this.add(this.createLastRunInfoPanel());
        }
    }

    private createButtonProcessLog(): Button {
        const props: Partial<ButtonProps> = {
            id: this.createInnerComponentId("buttonProcessLog"),
            onClick: () => this.buttonProcessLogOnClick()
        }

        if (this.isCompact) {
            return this.createCompactButton(props);
        } else {
            return new Button({
                ...props,
                caption: "Process Log",
                color: "primary",
                tooltip: "View detailed process log information",
                rowBreak: this.variant === PanelProcessLastRunVariant.stacked
            });
        }
    }

    private createCompactButton(props: Partial<ButtonProps>): Button {
        const button = new Button({
            ...props,
            imageName: "processLog",
            imageWidth: 24,
            imageHeight: 24,
            variant: ButtonVariant.round
        });
        button.tooltipCallback = () => {
            const panel = this.createLastRunInfoPanel();
            this.searchSourceProcessLog().then(() => {
                button.tooltipInstance = button.showTooltip(panel,
                    null,
                    {
                        themeKey: "quickInfo",
                        color: null
                    });
            });
            return null;
        }
        return button;
    }

    private createLastRunInfoPanel(): Panel {
        const panelLastRunInfo = new Panel({
            id: this.createInnerComponentId("panelLastRunInfo"),
            padding: 0,
            margin: 0,
            marginLeft: this.variant === PanelProcessLastRunVariant.inline ? 12 : 0
        });

        const textboxLastProcessed = new Textbox({
            id: this.createInnerComponentId("textboxLastProcessed"),
            caption: "Last Processed",
            field: "date_run",
            ...this.textboxProps
        });

        const textboxUser = new Textbox({
            id: this.createInnerComponentId("textboxUser"),
            caption: "User",
            field: "user_id",
            ...this.textboxProps
        });

        panelLastRunInfo.add(textboxLastProcessed, textboxUser);
        this.refreshLastProcessedData();
        return panelLastRunInfo;
    }

    private buttonProcessLogOnClick() {
        PanelProcessLastRun.processLogViewerHandler?.(this.processDescription);
    }

    public async refreshLastProcessedData() {
        // Avoids uncessesary search for the compact variant.
        // We always search the datasource before displaying the databound components in a tooltip.
        if (!this.isCompact) {
            return this.searchSourceProcessLog();
        }
    }

    private async searchSourceProcessLog() {
        return this.sourceProcessLog.search({descr: this.processDescription});
    }
}
